'use client';

import React from 'react';

import { animated, config, useSpring } from '@react-spring/web';

import { cn } from '~/lib/utils';

interface OrganGameAnswerProps {
  answer: string;
  index: number;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isIncorrectGuess: boolean;
  isCorrectGuess: boolean;
  isGameOver: boolean;
}

export const OrganGameAnswer = React.memo<OrganGameAnswerProps>(
  ({ answer, index, onClick, isIncorrectGuess, isCorrectGuess, isGameOver }) => {
    const [styles, api] = useSpring(() => ({
      transform: `scale(1.125) translate(0, -5%)`,
      opacity: 0,
    }));

    const isDisabled = isIncorrectGuess || isCorrectGuess || isGameOver;

    React.useEffect(() => {
      api.start({ opacity: 1, transform: `scale(1) translate(0px, 0px)`, delay: index * 100, config: config.gentle });
    }, [api, index]);

    React.useEffect(() => {
      if (isIncorrectGuess) {
        api.start({ transform: `scale(0.95)  translate(0px, 0px)` });
      }
      if (isGameOver && !isCorrectGuess) {
        api.start({ opacity: 0.5 });
      }
    }, [isIncorrectGuess, isCorrectGuess, isGameOver, api]);

    return (
      <animated.button
        className={cn(
          'font-greycliff-bold flex w-full items-center justify-start gap-x-3 rounded-lg border p-3 text-sm',
          !isIncorrectGuess && !isCorrectGuess && 'border-blue-light/30 bg-blue/5 text-blue',
          isIncorrectGuess && 'border-magenta/30 bg-magenta/5 text-primary line-through',
          isCorrectGuess && 'border-teal/30 bg-teal/5 text-teal',
        )}
        disabled={isDisabled}
        onClick={onClick}
        style={styles}
      >
        <svg fill='none' height='20' viewBox='0 0 20 20' width='20' xmlns='http://www.w3.org/2000/svg'>
          <circle
            cx='10'
            cy='10'
            fill={isCorrectGuess ? '#1BA5A4' : isIncorrectGuess ? '#ED255E' : 'none'}
            r='9'
            stroke={!isIncorrectGuess && !isCorrectGuess ? '#0071CE' : 'none'}
            strokeWidth={!isIncorrectGuess && !isCorrectGuess ? '2' : '1'}
          />
          <path
            className={!isCorrectGuess ? 'hidden' : ''}
            d='M14 7L8.5 12.5L6 10'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
          />
          <g className={!isIncorrectGuess ? 'hidden' : ''}>
            <path
              d='M13.3999 7.62988L7.3999 13.6299'
              stroke='white'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='1.76055'
            />
            <path
              d='M7.3999 7.62988L13.3999 13.6299'
              stroke='white'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='1.76055'
            />
          </g>
        </svg>
        <span>{answer}</span>
      </animated.button>
    );
  },
);

OrganGameAnswer.displayName = 'OrganGameAnswer';
